import { useMemo } from 'react';
import { DateTime } from 'luxon';
import { useQuery } from '@tanstack/react-query';
import { graphqlSdk } from '../Utils/graphql-client';
import useShopForPageType from './use-shop-for-page-type';

interface DurationOption {
  priceLow: number;
  priceHigh: number;
  durationString: any;
  durationNumber: number;
}

export interface OpenDay {
  date: number;
  capacity: number | null | undefined;
  priceModelId: string | null | undefined;
  isOpen: boolean;
  durations: DurationOption[];
}

const useOpenDays = (activeDate: Date | null) => {
  const shop = useShopForPageType();

  const variables = useMemo(() => {
    if (!activeDate || !shop?.id || !shop?.timezoneName) return undefined;

    return {
      shopId: shop?.id,
      after: DateTime.fromJSDate(activeDate)
        .setZone(shop?.timezoneName, { keepLocalTime: true })
        .startOf('month')
        // using toJSDate here because DateTime.toISO() returns string | null
        .toJSDate()
        .toISOString(),
      before: DateTime.fromJSDate(activeDate)
        .setZone(shop?.timezoneName, { keepLocalTime: true })
        .endOf('month')
        .toJSDate()
        .toISOString(),
    };
  }, [activeDate, shop?.id, shop?.timezoneName]);

  const query = useQuery({
    queryKey: ['businessDays', variables],
    queryFn: async () => {
      if (!variables) return [];

      const data = await graphqlSdk.getBusinessDays(variables);

      const openDays: OpenDay[] = data.getBusinessDays.map((day) => {
        return {
          date: DateTime.fromISO(day.date).setZone(shop?.timezoneName).day,
          capacity: day.capacity,
          priceModelId: day.priceModelId,
          isOpen: Boolean(day.UTCOpeningTime),
          durations: day.durations,
        };
      });
      console.log('useOpenDays days', openDays);

      return openDays;
    },
    enabled: Boolean(activeDate && shop?.id && shop?.timezoneName && variables),
    placeholderData: [],
    // Open days are considered fresh for 1 minute
    // A query with the same variables will at most be made every minute
    staleTime: 1000 * 60 * 1,
    // Set cache time to 5 minutes, which is the default
    cacheTime: 1000 * 60 * 5,
  });

  const futureOpenDays = useMemo(() => {
    if (!activeDate || !query.data) return [];

    const active = DateTime.fromJSDate(activeDate).setZone(shop?.timezoneName, {
      keepLocalTime: true,
    });
    const now = DateTime.now().setZone(shop?.timezoneName);
    let dateOnMonth = now.day;

    // If active month isn't current month, set dateOnMonth to 1
    // else keep as current date
    if (now.month !== active.month) dateOnMonth = 1;

    const futureDays = [];

    for (const day of query.data) {
      if (day.date >= dateOnMonth) futureDays.push(day);
    }

    return futureDays;
  }, [activeDate, shop?.timezoneName, query.data]);

  return { ...query, futureOpenDays };
};

export default useOpenDays;
